import React, { useEffect, useState } from 'react';
import { StatusFormSetting } from '../AssetTabs';
import '../../../AssetStatusForm.scss'
import LoaderOverlay from '../../common/Loader/LoaderOverlay';

interface FieldState {
    [key: string]: string;
}

type SetPageFunction = React.Dispatch<React.SetStateAction<{ [key: string]: FieldState; }>>;
type SetTabPageFunction = React.Dispatch<React.SetStateAction<string>>
type SetChangeFieldFunction = React.Dispatch<React.SetStateAction<{
    [formName: string]: Set<string>;
}>>

interface CellularConfigFormProps {
    formState: { [key: string]: FieldState };
    setFormState: SetPageFunction;
    setTabDivHeight: SetTabPageFunction;
    CellularConfigFormData: StatusFormSetting[] | undefined;
    changedFields: { [formName: string]: Set<string> };
    setChangedFields: SetChangeFieldFunction;
    formNames: string;
    isToggle: boolean;
    assetId: string;
    userTimeZone: string;
    handleLogout: () => void;
    isRefreshedClick: boolean;
}

interface InitialValues {
    [formName: string]: {
        [fieldName: string]: string;
    };
}


const CellularConfigForm: React.FC<CellularConfigFormProps> = ({ formState, setFormState, setTabDivHeight, handleLogout, CellularConfigFormData, changedFields, setChangedFields, formNames, isToggle, assetId, isRefreshedClick }) => {
    const [initialValues, setInitialValues] = useState<{ [formName: string]: { [fieldName: string]: string } }>({});
    const FormName = formNames
    const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT;
    const queryParams = {
        assetId: assetId
    };

    const queryString = new URLSearchParams(queryParams).toString();
    const getApiUrl = `${baseUrl}/SetPointsAggregation/GetCellularAttribiuteDataByAssetId?${queryString}`;
    const [isSetPointLoading, setIsSetPointLoading] = useState(true)

    const [cellularAttributeData, setCellularAttributeData] = useState([])

    const fetchCellularAttributeData = async () => {
        const headers = {
            'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
            'Content-Type': 'application/json' // Adjust content type if needed
        }
        try {
            const response = await fetch(getApiUrl, { headers });

            if (response.status === 401) {
                handleLogout()
            }

            if (!response.ok || response.status !== 200) {
                setIsSetPointLoading(false)
                setCellularAttributeData([])
            }
            const data = await response.json();

            if (data) {
                setCellularAttributeData(data)
            }
        } catch (error) {
        }
        finally {
            setIsSetPointLoading(false); // Set loading to false whether the fetch was successful or not      
        }
    };

    useEffect(() => {
        setTabDivHeight("1250px");
        fetchCellularAttributeData();
    }, [isRefreshedClick]);


    useEffect(() => {
        // Initialize initial values when the component mounts or when form data is initialized
        const initialValues: InitialValues = {};
        // Initialize initial values for each form and field
        // Assuming formState has been initialized with initial form data
        Object.keys(formState).forEach(formName => {
            initialValues[formName] = {};
            Object.keys(formState[formName]).forEach(fieldName => {
                initialValues[formName][fieldName] = formState[formName][fieldName];
            });
        });
        setInitialValues(initialValues);
        setChangedFields({})
    }, [FormName, isToggle]);

    // Handle input change for a specific field in a specific form
    const handleInputChange = (formName: string, fieldName: string, value: string) => {
        setFormState((prevFormState) => ({
            ...prevFormState,
            [formName]: {
                ...prevFormState[formName],
                [fieldName]: value,
            },
        }));

    };

    // Function to identify change in field value
    const handleFieldChange = (formName: string, fieldName: string, newValue: string) => {
        const initialValue = initialValues[formName]?.[fieldName];
        if (initialValue !== newValue) {
            setChangedFields((prevChangedFields) => ({
                ...prevChangedFields,
                [formName]: new Set<string>([...(prevChangedFields[formName] || []), fieldName]),
            }));
        } else if (initialValue === newValue) {
            setChangedFields((prevChangedFields) => {
                const updatedFields = new Set<string>(prevChangedFields[formName] || []);
                updatedFields.delete(fieldName); // Remove the field name if the value reverts back to the initial value
                return {
                    ...prevChangedFields,
                    [formName]: updatedFields,
                };
            });
        }
    };

    if (isSetPointLoading) {
        return <LoaderOverlay />
    }

    return (
        <div style={{ paddingLeft: "15px" }}>
            {/* === Common container style for both === */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    columnGap: "20px",
                    rowGap: "10px",
                }}
            >
                {CellularConfigFormData?.map(
                    ({ id, value, name, label, type, options, enable, description, unit }) => (
                        <div
                            key={id}
                            style={{
                                flex: "1 1 320px", //  flexible but consistent min width
                                maxWidth: "340px", // aligns with bottom section
                                boxSizing: "border-box",
                            }}
                        >
                            {enable && (
                                <div className="tooltip-container">
                                    <label
                                        style={{
                                            display: "block",
                                            marginBottom: "2px",
                                            marginLeft: "3px",
                                        }}
                                    >
                                        {label}
                                    </label>
                                    {description && <span className="tooltip-text">{description}</span>}
                                </div>
                            )}

                            {type !== "dropdown" ? (
                                <div className="relative w-full">
                                    <input
                                        type="number"
                                        value={formState[FormName]?.[name] ?? value}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            handleInputChange(FormName, name, newValue);
                                            handleFieldChange(FormName, name, newValue);
                                        }}
                                        style={{
                                            visibility: enable ? "visible" : "hidden",
                                            padding: "6px",
                                            color: "white",
                                            borderRadius: "8px",
                                            textAlign: "right",
                                            border: "1px solid #555",
                                            backgroundColor: changedFields[FormName]?.has(name)
                                                ? "rgb(208 196 64)"
                                                : "rgb(5 17 35)",
                                            width: "100%",
                                            boxSizing: "border-box",
                                        }}
                                        placeholder={label}
                                    />
                                </div>
                            ) : (
                                <select
                                    value={formState[FormName]?.[name] ?? value}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        handleInputChange(FormName, name, newValue);
                                        handleFieldChange(FormName, name, newValue);
                                    }}
                                    style={{
                                        visibility: enable ? "visible" : "hidden",
                                        padding: "6px",
                                        color: "white",
                                        borderRadius: "8px",
                                        textAlign: "left",
                                        border: "1px solid #555",
                                        backgroundColor: changedFields[FormName]?.has(name)
                                            ? "rgb(208 196 64)"
                                            : "rgb(5 17 35)",
                                        width: "100%",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {options?.map((option: any) => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )
                )}
            </div>

            {/* === Bottom Section (Cellular Attributes) === */}
            {cellularAttributeData.length > 0 &&
                <div style={{ marginTop: "20px" }}>
                    <label style={{ display: "block", marginBottom: "10px", marginLeft: "3px" }}>
                        Cellular attributes
                    </label>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            rowGap: "15px",
                        }}
                    >
                        {cellularAttributeData?.map(
                            ({ id, value, label, enable, status, description, color }) => (
                                <div
                                    key={id}
                                    style={{
                                        flex: "1 1", // same as above
                                        maxWidth: "340px", // ensures exact alignment
                                        width: "100%",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {enable && (
                                        <div className="tooltip-container">
                                            <label
                                                style={{
                                                    display: "block",
                                                    marginBottom: "4px",
                                                    marginLeft: "3px",
                                                    color: "white",
                                                }}
                                            >
                                                {label}
                                            </label>
                                            {description && <span className="tooltip-text">{description}</span>}
                                        </div>
                                    )}

                                    <div style={{ position: "relative", width: "100%" }}>
                                        <span
                                            style={{
                                                position: "absolute",
                                                left: "10px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                color: color,
                                                pointerEvents: "none",
                                            }}
                                        >
                                            {status}
                                        </span>

                                        <span
                                            style={{
                                                position: "absolute",
                                                right: "4px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                color: color,
                                                border: value ? `1px solid ${color}` : "",
                                                borderRadius: "4px",
                                                padding: "2px 4px",
                                                pointerEvents: "none",
                                                visibility: enable ? "visible" : "hidden"
                                            }}
                                        >
                                            {value || ""}
                                        </span>

                                        <input
                                            type="text"
                                            value={""}
                                            style={{
                                                width: "100%",
                                                padding: "6px 50px",
                                                color: color,
                                                border: "1px solid #555",
                                                borderRadius: "8px",
                                                backgroundColor: "rgb(5 17 35)",
                                                textAlign: "center",
                                                visibility: enable ? "visible" : "hidden",
                                                boxSizing: "border-box",
                                            }}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            }
        </div>
    );
};

export default CellularConfigForm;
